import axios from "axios";
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import _ from 'helpers/no_conflict_lodash';
import { formatDate } from 'helpers/formatters/date_time_formats';

import { setCompaniesGroupFilterIds } from './query';
import { retrieveCompanies, setCompanyTags } from './search';
import { toggleAlert } from './ui';
import { changeSelectedCompanies } from './select_list';

import TagsApi from 'bundles/tags/tags_api';
import taskApi from 'components/tasks/api';

import { post } from 'helpers/api';

export const ADD_GROUP = 'COMPANIES-ADD_GROUP';
export function addGroup(group) {
  return {
    group,
    type: ADD_GROUP
  };
}

export const CHANGE_GROUPS = 'COMPANIES-CHANGE_GROUPS';
export function changeGroups(groups) {
  return {
    groups,
    type: CHANGE_GROUPS
  };
}

export const END_BULK_TAGGING = 'END_BULK_TAGGING';
export function endBulkTagging() {
  return { type: END_BULK_TAGGING };
}

export const END_EDIT_GROUPS = 'COMPANIES-END_EDIT_GROUPS';
export function endEditGroups() {
  return { type: END_EDIT_GROUPS };
}

export const END_BULK_DELETE = 'END_BULK_DELETE';
export function endBulkDelete() {
  return { type: END_BULK_DELETE };
}

export const END_DELETE_GROUPS = 'COMPANIES-END_DELETE_GROUPS';
export function endDeleteGroups() {
  return { type: END_DELETE_GROUPS };
}

export const END_EMAIL_GROUP = 'COMPANIES-END_EMAIL_GROUP';
export function endEmailGroup() {
  return { type: END_EMAIL_GROUP };
}

export const END_NEW_CALL_LIST = 'END_NEW_CALL_LIST';
export function endNewCallList() {
  return { type: END_NEW_CALL_LIST };
}

export const SAVE_ACTIVITY = 'COMPANIES-SAVE_ACTIVITY';
export function saveActivity(saved) {
  return {
    saved,
    type: SAVE_ACTIVITY
  };
}

export const SET_ACTIVITY = 'COMPANIES-SET_ACTIVITY';
export function setActivity(activity) {
  return {
    activity,
    type: SET_ACTIVITY
  };
}

export const SET_NOTES = 'COMPANIES-SET_NOTES';
export function setNotes(notes) {
  return {
    notes,
    type: SET_NOTES
  };
}

export const SET_TASKS = 'COMPANIES-SET_TASKS';
export function setTasks(tasks) {
  return {
    tasks,
    type: SET_TASKS
  };
}

export const SET_BULK_TAGS = 'COMPANIES-SET_BULK_TAGS';
export function setBulkTags(tags) {
  return { tags, type: SET_BULK_TAGS };
}

export const START_BULK_DELETE = 'START_BULK_DELETE';
export function startBulkDelete() {
  return { type: START_BULK_DELETE };
}

export const START_NEW_CALL_LIST = 'START_NEW_CALL_LIST';
export function startNewCallList() {
  return { type: START_NEW_CALL_LIST };
}

export const START_BULK_TAGGING = 'START_BULK_TAGGING';
export function startBulkTagging() {
  return { type: START_BULK_TAGGING };
}

export const START_EDIT_GROUPS = 'COMPANIES-START_EDIT_GROUPS';
export function startEditGroups(groups) {
  return {
    groups,
    type: START_EDIT_GROUPS
  };
}

export const START_EMAIL_GROUP = 'COMPANIES-START_EMAIL_GROUP';
export function startEmailGroup(emails) {
  return {
    emails,
    type: START_EMAIL_GROUP
  };
}

export const START_DELETE_GROUPS = 'COMPANIES-START_DELETE_GROUPS';
export function startDeleteGroups(groups) {
  return {
    groups,
    type: START_DELETE_GROUPS
  };
}

export function removeTag(tag, person) {
  const tagsPath = {
    tags: person.tagsPath
  };
  const api = new TagsApi(tagsPath);
  return dispatch => (
    api.deleteTag(tag).then((newTags) => {
      dispatch(setCompanyTags(person.id, newTags));
    }));
}

export function addTag(tag, person) {
  const tagsPath = {
    tags: person.tagsPath
  };
  const api = new TagsApi(tagsPath);
  return dispatch => (
    api.saveTag(tag).then((newTags) => {
      dispatch(setCompanyTags(person.id, newTags));
    }));
}

export function addToGroup(group, ids) {
  return (dispatch) => {
    return axios.patch(
      group.url,
      {
        member_ids: ids
      }
    ).then(() => dispatch(retrieveGroups()));
  };
}

export function bulkTagging(tags, companyIds) {
  return (dispatch, getState) => (
    axios.put(getState().paths.tags.bulkTagging, {
      tags: tags,
      company_ids: companyIds,
    }).then((response) => {
      if (response.status == 200) {
        const text = companyIds.length > 1 ? " Companies were Tagged" : " Company was Tagged";
        dispatch(toggleAlert(companyIds.length + text, false));
      } else {
        const text = companyIds.length > 1 ? " Companies weren't Tagged" : " Company was not Tagged";
        dispatch(toggleAlert(companyIds.length + text, true));
      }
    }));
}

export function createBulkTask(task, ids) {
  return dispatch => (
    taskApi.bulkCreate(task, ids).then((result) => {
      dispatch(toggleAlert(result.message, false));
    })
  );
}

export function clearTasks() {
  return (dispatch) => {
    dispatch(setNotes(""));
    dispatch(saveActivity(false));
  };
}

export function createGroup(group, ids) {
  return (dispatch, getState) => {
    const { id, ...groupAttributes } = snakecaseKeys(group);
    return axios.post(
      getState().paths.companies.groups,
      {
        group: groupAttributes,
        member_ids: ids
      }
    ).then((response) => {
      dispatch(addGroup(camelcaseKeys(response.data.group)));
      dispatch(endEditGroups());
      dispatch(setCompaniesGroupFilterIds([response.data.group.id]));
      dispatch(retrieveCompanies());
      return response.data.group;
    });
  };
}

export function deleteFromGroup(group, ids) {
  return (dispatch) => {
    return axios.patch(
      group.deleteMembersUrl,
      {
        member_ids: ids
      }
    ).then(() => dispatch(retrieveGroups()));
  };
}

export function deleteGroups(groups) {
  return (dispatch) => {
    const requests = groups.map(group => axios.delete(group.url));
    return Promise.all(requests).then(() => {
      dispatch(endDeleteGroups());
      dispatch(retrieveGroups());
    });
  };
}

export function getTasks(assigneeId) {
  return (dispatch, getState) => {
    const path = getState().paths.tasks;
    return axios.get(
      path,
      {
        params: {
          "q[completed]": true,
          "q[task_associations_associated_model_id_eq]": assigneeId,
          "q[task_associations_associated_model_type_eq]": "Contacts::Company"
        }
      }
    ).then((response) => {
      const tasks = _.map(response.data.tasks, tasks => tasksFromApi(tasks.task));
      dispatch(setTasks(tasks));
    });
  };
}

export function newGroup() {
  return startEditGroups([{ id: null, name: '' }]);
}

export function retrieveGroups() {
  return (dispatch, getState) => (
    axios.get(getState().paths.companies.groups).then(response => (
      dispatch(changeGroups(_.map(response.data.groups, group => camelcaseKeys(group))))
    ))
  );
}

export function bulkDelete() {
  return (dispatch, getState) => {
    const deletePath = getState().paths.companies.bulkDelete;
    const companyIds = getState().selectList.selectedCompanyIds;

    post(deletePath, { contactIds: companyIds }).then(({ message, error }) => {
      dispatch(endBulkDelete());
      dispatch(retrieveCompanies());
      dispatch(toggleAlert(message, error));
      dispatch(changeSelectedCompanies([]));
    });
  };
}

export function sendNotes(path) {
  return (dispatch, getState) => {
    return axios.patch(
      path,
      {
        contacts_company: {
          comments_attributes: {
            0: {
              body: getState().companies.activityNotes
            }
          }
        }
      }
    ).then(() => {
      dispatch(setNotes(""));
      dispatch(saveActivity(true));
    });
  };
}

export function sendTask(isCompleted, activity) {
  return () => {
    taskApi.addTask(
      {
        taskType: activity.taskType,
        completed: isCompleted,
        logged: isCompleted,
        title: activity.taskSummary,
        body: activity.taskDetails,
        assigneeId: activity.assigneeDatabaseMember.value,
        dueDate: activity.taskDate.toString(),
        associatedModels: associateToTask(activity.associatedModels)
      }
    );
  };
}

function associateToTask(associatedModels) {
  return associatedModels.map((model) => {
    return {
      associatedModelId: model.id,
      associatedModelType: model.modelType,
      _destroy: false
    };
  });
}

export function updateGroups(groups) {
  return (dispatch) => {
    const requests = groups.map((group) => {
      const { url, ...groupAttributes } = snakecaseKeys(group);
      return axios.patch(url, { group: groupAttributes });
    });

    return Promise.all(requests).then(() => {
      dispatch(endEditGroups());
      dispatch(retrieveGroups());
    });
  };
}

function tasksFromApi(apiTask) {
  return {
    body: apiTask.body,
    completedAt: formatDate(apiTask.completedAt),
    id: apiTask.id,
    type: apiTask.task_type,
    title: apiTask.title
  };
}
